import { bandFor, CHORE_KIND_GLYPH, CHORE_KIND_NAMES, CHORE_KINDS, suggestChoreKind, type ChoreKind } from '@kk/domain';
import { useQuery } from '@tanstack/react-query';
import { Badge, Card, Eyebrow, Icon, SectionLabel, Text, TextField, WasabiButton } from '@kk/ui';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useOutletContext, useParams } from 'react-router';

import { useClients, useMembership, useSession } from '@/auth/SessionProvider';
import { Avatar, LinkButton, Status } from '@/components/bits';
import { deck } from '@/copy/deck';
import { placeholders } from '@/copy/placeholders';
import {
  useErrorMessage,
  useAssignment,
  useCreateAssignment,
  usePublishAssignment,
  useRoster,
  useUpdateAssignment,
} from '@/data/hooks';
import { isoDateLabel, toIsoDate } from '@/lib/time';
import type { ShellContext } from '@/shell/Shell';
import type { Assignment, AssignmentInput, TaskApprover } from '@/types/wire';

interface TaskDraft {
  key: number;
  id?: string;
  title: string;
  reps: number;
  approver: TaskApprover;
  /**
   * 03c: the category this row's select shows when somebody CHOSE it — a staff member here, or the club earlier
   * (stored `confirmed`). Absent, the select follows the title's suggestion (`taskKind`).
   */
  kind?: ChoreKind;
}

/**
 * **03c · A TASK ROW'S CATEGORY** (docs/briefs/03-chore-categories.md, Requirements 8; the board's 03c). The select is
 * filled in from the task's title by the same function the server runs, and follows the title until somebody picks.
 * `unsure` is the amber outline: no keyword matched and nobody has picked yet. A row with no title is neither.
 */
export function taskKind(t: Pick<TaskDraft, 'title' | 'kind'>): { kind: ChoreKind; unsure: boolean } {
  if (t.kind) return { kind: t.kind, unsure: false };
  const s = suggestChoreKind(t.title);
  return { kind: s.kind, unsure: !s.confident && t.title.trim() !== '' };
}

/** Publish waits while any titled row is still amber; a draft can be saved with one (Evan, 9 Oct). */
export const hasUnsureTask = (form: BuilderForm): boolean => form.tasks.some((t) => t.title.trim() && taskKind(t).unsure);

interface RewardDraft {
  title: string;
  /** The number field's text; '' = no score gate. */
  minScore: string;
}
export interface BuilderForm {
  title: string;
  start: string;
  end: string;
  tasks: TaskDraft[];
  reward: RewardDraft | null;
  groupIds: string[];
  childIds: string[];
}

let nextKey = 1;
const newTask = (): TaskDraft => ({ key: nextKey++, title: '', reps: 1, approver: 'org' });

/** A blank draft: one task, this week's window (today → +4 days, the board's Mon–Fri shape), no targets. */
export function emptyForm(now = new Date()): BuilderForm {
  const end = new Date(now);
  end.setDate(end.getDate() + 4);
  return {
    title: '',
    start: toIsoDate(now),
    end: toIsoDate(end),
    tasks: [newTask()],
    reward: null,
    groupIds: [],
    childIds: [],
  };
}

export function formFrom(a: Assignment): BuilderForm {
  return {
    title: a.title,
    start: a.window.start,
    end: a.window.end,
    // a category somebody chose is kept as chosen; a stored suggestion keeps following the title (03c)
    tasks: a.tasks.map(({ kind, kindStatus, ...t }) => ({
      key: nextKey++,
      ...t,
      ...(kind && kindStatus === 'confirmed' ? { kind } : {}),
    })),
    reward: a.reward
      ? {
          title: a.reward.title,
          minScore: a.reward.minScore == null ? '' : String(a.reward.minScore),
        }
      : null,
    groupIds: [...a.groupIds],
    childIds: [...a.childIds],
  };
}

/** The 0–100 gate the field holds, or null when empty / not a whole number in range. */
export function parseMinScore(text: string): number | null {
  if (text.trim() === '') return null;
  const n = Number(text);
  return Number.isInteger(n) && n >= 0 && n <= 100 ? n : null;
}

/**
 * The wire body for "Save draft" (POST) and later saves (PATCH: the same shape; `reward: null` removes one).
 * Returns null while the form cannot be saved: no title, no titled task, an end before the start, or a
 * reward that is open but unusable (no name, or a gate that is not a whole 0–100) — a reward must never be
 * dropped silently, so those cases raise the VALIDATION notice instead.
 */
export function toInput(form: BuilderForm, categories = false): AssignmentInput | null {
  const title = form.title.trim();
  const tasks = form.tasks
    .filter((t) => t.title.trim())
    .map((t) => ({
      ...(t.id ? { id: t.id } : {}),
      title: t.title.trim(),
      reps: t.reps,
      approver: t.approver,
      // 03c: the category the row shows is the club's pick and is saved as such — unless it is still amber, which
      // nobody has chosen: that one is left for the server to suggest, so it comes back amber
      ...(categories && !taskKind(t).unsure ? { kind: taskKind(t).kind } : {}),
    }));
  if (!title || tasks.length === 0 || !form.start || !form.end || form.end < form.start)
    return null;
  const minScore = form.reward ? parseMinScore(form.reward.minScore) : null;
  const rewardTitle = form.reward?.title.trim() ?? '';
  if (form.reward) {
    if (!rewardTitle) return null;
    if (form.reward.minScore.trim() !== '' && minScore === null) return null;
  }
  return {
    title,
    window: { start: form.start, end: form.end },
    tasks,
    reward: form.reward
      ? { title: rewardTitle, unlockRule: 'all_tasks', ...(minScore !== null ? { minScore } : {}) }
      : null,
    groupIds: form.groupIds,
    childIds: form.childIds,
  };
}

function ApproverChips({
  value,
  onChange,
  disabled,
  index,
}: {
  value: TaskApprover;
  onChange: (v: TaskApprover) => void;
  disabled: boolean;
  index: number;
}) {
  const chip = (approver: TaskApprover, label: string) => {
    const selected = value === approver;
    return (
      <button
        type="button"
        className={`approver-chip${selected ? ' selected' : ''}`}
        aria-pressed={selected}
        disabled={disabled}
        onClick={() => onChange(approver)}
        data-testid={`approver-${approver}-${index}`}
      >
        <span>
          <Text variant={selected ? 'chipSelected' : 'chip'} tone={selected ? 'white' : 'slate'}>
            {label}
          </Text>
        </span>
      </button>
    );
  };
  return (
    <div className="approver">
      {chip('org', deck.o03.orgApproves)}
      {chip('parent', deck.o03.parentApproves)}
    </div>
  );
}

/** O03 · /assignments/new and /assignments/:id (a draft; a published one opens read-only). */
export function AssignmentBuilderScreen() {
  const errorMessage = useErrorMessage();
  const { org, isPhone } = useOutletContext<ShellContext>();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { session } = useSession();
  const membership = useMembership();
  const existing = useAssignment(id ?? null);
  const roster = useRoster();
  const create = useCreateAssignment();
  const update = useUpdateAssignment();
  const publish = usePublishAssignment();

  const [form, setForm] = useState<BuilderForm>(() => emptyForm());
  const [saved, setSaved] = useState<Assignment | null>(null);
  const [loadedFor, setLoadedFor] = useState<string | null>(null);
  const [pickChildren, setPickChildren] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);

  // /assignments/:id → /assignments/new (browser back/forward, or a future "New assignment" here) is a
  // blank builder: without this the next save would PATCH the draft the user just left.
  const previousId = useRef(id);
  useEffect(() => {
    const was = previousId.current;
    previousId.current = id;
    if (was && !id) {
      setForm(emptyForm());
      setSaved(null);
      setLoadedFor(null);
      setPickChildren(false);
      setNotice(null);
    }
  }, [id]);

  // Load the draft once per id; a draft this screen just created is already in `saved`.
  useEffect(() => {
    if (existing.data && loadedFor !== existing.data.id) {
      setForm(formFrom(existing.data));
      setSaved(existing.data);
      setLoadedFor(existing.data.id);
      setPickChildren(existing.data.childIds.length > 0);
    }
  }, [existing.data, loadedFor]);

  const readOnly = saved?.published === true;
  const busy = create.isPending || update.isPending || publish.isPending;
  // 03c is behind CHORE_CATEGORIES: closed, the builder looks and saves exactly as it did
  const { data: clients } = useClients();
  const config = useQuery({ queryKey: ['public', 'config'], queryFn: () => clients.publicConfig(), staleTime: 5 * 60_000 });
  const categories = config.data?.choreCategories === 'open';
  const input = useMemo(() => toInput(form, categories), [form, categories]);
  const dirty = useMemo(
    () => !saved || JSON.stringify(input) !== JSON.stringify(toInput(formFrom(saved), categories)),
    [input, saved],
  );
  const childCount = saved?.childCount ?? 0;
  const canPublish = !!saved && !readOnly && !dirty && childCount > 0 && !busy && !(categories && hasUnsureTask(form));

  const patch = (p: Partial<BuilderForm>) => setForm((f) => ({ ...f, ...p }));
  const patchTask = (key: number, p: Partial<TaskDraft>) =>
    patch({ tasks: form.tasks.map((t) => (t.key === key ? { ...t, ...p } : t)) });

  const onSave = async () => {
    if (!input) {
      setNotice('VALIDATION');
      return;
    }
    // The URL names a draft that is not in hand: saving here would POST a second assignment.
    if (id && !saved) return;
    setNotice(null);
    try {
      // The form adopts the saved draft (server task ids, trimmed strings) so the dirty check clears.
      if (saved) {
        const updated = await update.mutateAsync({ id: saved.id, patch: input });
        setSaved(updated);
        setForm(formFrom(updated));
      } else {
        const created = await create.mutateAsync(input);
        setSaved(created);
        setForm(formFrom(created));
        setLoadedFor(created.id);
        navigate(`/assignments/${encodeURIComponent(created.id)}`, { replace: true });
      }
      setNotice(placeholders.builder.saved);
    } catch (e) {
      setNotice(errorMessage(e));
    }
  };
  const onPublish = async () => {
    if (!saved) return;
    setNotice(null);
    try {
      await publish.mutateAsync(saved.id);
      navigate('/assignments');
    } catch (e) {
      setNotice(errorMessage(e));
    }
  };

  const groups = org?.groups ?? [];
  const children = useMemo(() => roster.data?.children ?? [], [roster.data]);
  const targeted = children.filter(
    (r) =>
      form.childIds.includes(r.child.id) || r.child.groupIds.some((g) => form.groupIds.includes(g)),
  );
  const previewChild = (targeted[0] ?? children[0])?.child;
  const staffName = membership?.displayName ?? session?.user.name ?? session?.user.email ?? '';
  // the preview's org badge is a TAG — "BGC" — so it reads initials, never the readable short form:
  // with the three-name split deployed, shortName is "BGC Decatur" and would not fit the badge
  const orgBadge = org?.initials;
  const previewTitle = form.tasks.find((t) => t.title.trim())?.title.trim() ?? form.title.trim();

  const status = id && existing.isPending ? placeholders.empty.loading : null;
  const loadError = existing.error ? errorMessage(existing.error) : null;
  /** The URL names a draft that has not loaded (still in flight, or the GET failed). */
  const unloaded = !!id && loadedFor !== id;

  const buttons = (
    <>
      <WasabiButton
        label={deck.o03.saveDraft}
        variant="outline"
        textSize="sm"
        faceHeight={isPhone ? 52 : 44}
        fullWidth={isPhone}
        disabled={readOnly || busy || !dirty}
        loading={create.isPending || update.isPending}
        onPress={() => void onSave()}
        testID="save-draft"
      />
      <WasabiButton
        label={deck.o03.publish(childCount)}
        size={isPhone ? 'md' : 'sm'}
        fullWidth={isPhone}
        disabled={!canPublish}
        loading={publish.isPending}
        onPress={() => void onPublish()}
        testID="publish"
      />
      {categories && hasUnsureTask(form) && !readOnly ? (
        <Text variant="bodySm" tone="slate" testID="category-needed">
          {deck.o03.categoryNeeded}
        </Text>
      ) : null}
    </>
  );

  const header = (
    <div className="page-header">
      <div className="page-header-text builder-title">
        <Eyebrow tone="muted">{deck.o03.eyebrow}</Eyebrow>
        <TextField
          placeholder={deck.o03.titleSample}
          value={form.title}
          onChangeText={(t) => patch({ title: t })}
          editable={!readOnly}
          accessibilityLabel={placeholders.builder.titleField}
          testID="title"
        />
      </div>
      {isPhone ? null : <div className="actions">{buttons}</div>}
    </div>
  );

  const notices = (
    <>
      {status ? <Status>{status}</Status> : null}
      {loadError ? <Status testID="load-error">{loadError}</Status> : null}
      {readOnly ? <Status testID="read-only">{placeholders.builder.readOnly}</Status> : null}
      {notice ? <Status testID="notice">{notice}</Status> : null}
    </>
  );

  const when = (
    <Card variant="quiet" radius="row" padding={18}>
      <div className="stack">
        <SectionLabel>{deck.o03.when}</SectionLabel>
        <div className="field-pair">
          {(['start', 'end'] as const).map((k) => (
            <div className="date-field" key={k}>
              <input
                type="date"
                className="date-input"
                value={form[k]}
                disabled={readOnly}
                aria-label={
                  k === 'start' ? placeholders.builder.startDate : placeholders.builder.endDate
                }
                onChange={(e) => patch({ [k]: e.target.value })}
                data-testid={`date-${k}`}
              />
              <Text variant="meta" tone="muted">
                {isoDateLabel(form[k])}
              </Text>
            </div>
          ))}
        </div>
      </div>
    </Card>
  );

  const tasks = (
    <Card variant="quiet" radius="row" padding={18}>
      <div className="stack">
        <div className="section-head">
          <SectionLabel>{deck.o03.tasks(form.tasks.length)}</SectionLabel>
          <LinkButton
            label={deck.o03.addTask}
            disabled={readOnly}
            onPress={() => patch({ tasks: [...form.tasks, newTask()] })}
            testID="add-task"
          />
        </div>
        {categories ? (
          <Text variant="bodySm" tone="slate" testID="category-help">
            {deck.o03.categoryHelp}
          </Text>
        ) : null}
        {form.tasks.map((t, i) => (
          <div className={categories ? 'task-row task-row-kinds' : 'task-row'} key={t.key} data-testid={`task-${i}`}>
            <TextField
              placeholder={placeholders.builder.taskTitle}
              value={t.title}
              onChangeText={(title) => patchTask(t.key, { title })}
              editable={!readOnly}
              testID={`task-title-${i}`}
            />
            {categories ? (
              readOnly ? (
                <div className="kind-field kind-field-read">
                  <span className="kind-icon" aria-hidden="true" data-testid={`task-kind-icon-${i}`} data-glyph={CHORE_KIND_GLYPH[taskKind(t).kind]}>
                    <Icon name={CHORE_KIND_GLYPH[taskKind(t).kind]} size={18} />
                  </span>
                  <Text variant="bodySm" tone="slate" testID={`task-kind-${i}`}>
                    {CHORE_KIND_NAMES[taskKind(t).kind].name}
                  </Text>
                </div>
              ) : (
                // the row's category with its icon (the board's 03c; the six are CHORE_KIND_GLYPH, the app's own).
                // A browser's own menu cannot draw an icon beside each choice, so the icon is the chosen one's.
                <div className="kind-field">
                  <span className="kind-icon" aria-hidden="true" data-testid={`task-kind-icon-${i}`} data-glyph={CHORE_KIND_GLYPH[taskKind(t).kind]}>
                    <Icon name={CHORE_KIND_GLYPH[taskKind(t).kind]} size={18} />
                  </span>
                  <select
                    className={taskKind(t).unsure ? 'kind-select kind-select-unsure' : 'kind-select'}
                    aria-label={deck.o03.category}
                    required
                    value={taskKind(t).kind}
                    onChange={(e) => patchTask(t.key, { kind: e.target.value as ChoreKind })}
                    data-testid={`task-kind-${i}`}
                    data-unsure={taskKind(t).unsure ? 'true' : 'false'}
                  >
                    {CHORE_KINDS.map((k) => (
                      <option key={k} value={k}>
                        {CHORE_KIND_NAMES[k].name}
                      </option>
                    ))}
                  </select>
                </div>
              )
            ) : null}
            <div className="stepper">
              <button
                type="button"
                className="stepper-button"
                aria-label={placeholders.builder.fewerReps}
                disabled={readOnly || t.reps <= 1}
                onClick={() => patchTask(t.key, { reps: t.reps - 1 })}
                data-testid={`reps-less-${i}`}
              >
                <Text variant="cardTitle" tone="slate">
                  −
                </Text>
              </button>
              <Text variant="bodySm" tone="slate" testID={`reps-${i}`}>
                {deck.o03.reps(t.reps)}
              </Text>
              <button
                type="button"
                className="stepper-button"
                aria-label={placeholders.builder.moreReps}
                disabled={readOnly || t.reps >= 99}
                onClick={() => patchTask(t.key, { reps: t.reps + 1 })}
                data-testid={`reps-more-${i}`}
              >
                <Text variant="cardTitle" tone="slate">
                  +
                </Text>
              </button>
            </div>
            <ApproverChips
              value={t.approver}
              onChange={(approver) => patchTask(t.key, { approver })}
              disabled={readOnly}
              index={i}
            />
            <button
              type="button"
              className="remove-button"
              aria-label={placeholders.builder.remove}
              disabled={readOnly || form.tasks.length <= 1}
              onClick={() => patch({ tasks: form.tasks.filter((x) => x.key !== t.key) })}
              data-testid={`remove-task-${i}`}
            >
              <Text variant="cardTitle" tone="slate">
                ×
              </Text>
            </button>
          </div>
        ))}
        <Text variant="meta" tone="muted">
          {deck.o03.rule}
        </Text>
      </div>
    </Card>
  );

  const gate = form.reward ? parseMinScore(form.reward.minScore) : null;
  const reward = (
    <Card variant="quiet" radius="row" padding={18}>
      <div className="stack">
        <div className="section-head">
          <SectionLabel>{deck.o03.reward}</SectionLabel>
          {form.reward ? (
            <LinkButton
              label={placeholders.builder.remove}
              disabled={readOnly}
              onPress={() => patch({ reward: null })}
              testID="remove-reward"
            />
          ) : (
            <LinkButton
              label={deck.o03.addReward}
              disabled={readOnly}
              onPress={() => patch({ reward: { title: '', minScore: '' } })}
              testID="add-reward"
            />
          )}
        </div>
        {form.reward ? (
          <>
            <div className="reward-cells">
              <div className="reward-cell">
                <SectionLabel>{deck.o03.name}</SectionLabel>
                <TextField
                  value={form.reward.title}
                  onChangeText={(title) =>
                    patch({ reward: { title, minScore: form.reward?.minScore ?? '' } })
                  }
                  editable={!readOnly}
                  accessibilityLabel={deck.o03.name}
                  testID="reward-title"
                />
              </div>
              <div className="reward-cell">
                <SectionLabel>{deck.o03.unlocksWhen}</SectionLabel>
                <Text variant="rowTitleSm" tone="ink">
                  {deck.o03.allTasksDone}
                </Text>
              </div>
            </div>
            <div className="gate-note">
              <div className="gate-line">
                <Text variant="rowTitleSm" tone="ink">
                  {deck.o03.alsoRequires}
                </Text>
                <input
                  type="number"
                  className="number-input"
                  min={0}
                  max={100}
                  step={1}
                  value={form.reward.minScore}
                  disabled={readOnly}
                  aria-label={placeholders.builder.minScore}
                  onChange={(e) =>
                    patch({ reward: { title: form.reward?.title ?? '', minScore: e.target.value } })
                  }
                  data-testid="min-score"
                />
                {gate !== null ? (
                  <Text variant="rowTitleSm" tone="ink" testID="band">
                    {deck.o03.band(config.data?.scoreEngineV2 === 'open' && bandFor(gate) === 'Building' ? deck.o03.bandStarting : bandFor(gate))}
                  </Text>
                ) : null}
              </div>
              <Text variant="meta" tone="slate">
                {deck.o03.checkedOnDevice}
              </Text>
            </div>
          </>
        ) : null}
      </div>
    </Card>
  );

  const toggle = (list: string[], id: string) =>
    list.includes(id) ? list.filter((x) => x !== id) : [...list, id];
  const assignTo = (
    <Card variant="quiet" radius="row" padding={18}>
      <div className="stack">
        <SectionLabel>{deck.o03.assignTo}</SectionLabel>
        {groups.map((g) => (
          <label className="check-row" key={g.id}>
            <input
              type="checkbox"
              checked={form.groupIds.includes(g.id)}
              disabled={readOnly}
              onChange={() => patch({ groupIds: toggle(form.groupIds, g.id) })}
              data-testid={`group-${g.id}`}
            />
            <Text variant="rowTitleSm" tone="ink" style={{ flex: 1 }}>
              {g.name}
            </Text>
            <Text variant="meta" tone="muted">
              {String(g.childCount)}
            </Text>
          </label>
        ))}
        <LinkButton
          label={deck.o03.pickChildren}
          onPress={() => setPickChildren((p) => !p)}
          testID="pick-children"
        />
        {pickChildren
          ? children.map((r) => (
              <label className="check-row" key={r.child.id}>
                <input
                  type="checkbox"
                  checked={form.childIds.includes(r.child.id)}
                  disabled={readOnly}
                  onChange={() => patch({ childIds: toggle(form.childIds, r.child.id) })}
                  data-testid={`child-${r.child.id}`}
                />
                <Avatar child={r.child} size={28} />
                <Text variant="rowTitleSm" tone="ink" style={{ flex: 1 }}>
                  {r.child.firstName}
                </Text>
              </label>
            ))
          : null}
        {pickChildren && roster.isPending ? <Status>{placeholders.empty.loading}</Status> : null}
      </div>
    </Card>
  );

  const preview = previewChild ? (
    <Card variant="quiet" radius="row" padding={18}>
      <div className="stack">
        <SectionLabel>{deck.o03.preview(previewChild.firstName)}</SectionLabel>
        <Card variant="playful" radius="row" padding={14}>
          <div className="preview-card" data-testid="preview">
            <Avatar child={previewChild} size={48} />
            <div className="cell grow">
              <Text variant="rowTitle" tone="ink" numberOfLines={2}>
                {previewTitle}
              </Text>
              <div className="preview-meta">
                {orgBadge ? <Badge label={orgBadge} tone="ink" /> : null}
                <Text variant="meta" tone="muted" style={{ flex: 1 }}>
                  {deck.o03.previewLine(form.title.trim(), staffName)}
                </Text>
              </div>
            </div>
          </div>
        </Card>
        {/* the club's own short name, as a pre-reader hears it (the 23 Oct audit, T1-7) */}
        <Text variant="meta" tone="muted" testID="preview-note">
          {deck.o03.previewNote(org?.shortName ?? membership?.orgName ?? org?.name ?? '')}
        </Text>
      </div>
    </Card>
  ) : null;

  // While the named draft is not in hand there is no form: typing would be overwritten by the arriving
  // draft, and a save would create a second assignment instead of editing this one.
  if (unloaded) return notices;

  if (isPhone)
    return (
      <>
        {header}
        {notices}
        {when}
        {tasks}
        {reward}
        {assignTo}
        {preview}
        {readOnly ? null : (
          <>
            <div className="footer-spacer" aria-hidden="true" />
            <div className="sticky-footer">
              <div className="stack">{buttons}</div>
            </div>
          </>
        )}
      </>
    );

  return (
    <>
      {header}
      {notices}
      <div className="builder-grid">
        <div className="stack">
          {when}
          {tasks}
          {reward}
        </div>
        <div className="stack">
          {assignTo}
          {preview}
        </div>
      </div>
    </>
  );
}
